import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Database,
  ArrowRight,
  Building2,
  FileCode,
  Layers,
  Brain,
  BarChart3,
  Zap,
} from "lucide-react";

const dataStats = [
  { label: "Canadian Charities", value: "83,301", description: "Complete entity registry" },
  { label: "Grant Programs", value: "605+", description: "Structured & ML-ready" },
  { label: "Unique Givers", value: "38,654", description: "5-year giving data" },
  { label: "ML Features", value: "22", description: "7-dimensional framework" },
];

const features = [
  {
    icon: Building2,
    title: "Canadian Entity Registry",
    description: "Complete profiles of all 83K+ registered charities with GIVE-side and RECEIVE-side metrics from 5 years of T3010 data.",
  },
  {
    icon: FileCode,
    title: "Grant Program Database",
    description: "600+ programs from GrantConnect and GrantStation with parsed eligibility criteria, deadlines, and requirements.",
  },
  {
    icon: Layers,
    title: "7-Dimensional Feature Framework",
    description: "22 data-backed features across Context, Range, Prioritization, Normalization, Time Horizon, Bind, and Bind Strength.",
  },
  {
    icon: Brain,
    title: "Semantic Embeddings & L1-L4",
    description: "Vector embeddings for mission matching and structured goal hierarchies (WHY, WHAT, HOW, WITH WHAT).",
  },
];

export const DataIntelPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 gradient-hero" data-testid="dataintel-hero">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-6 bg-blue-100 text-blue-700 hover:bg-blue-100 border-0 px-4 py-1.5 text-sm font-medium">
                  <Database className="h-3.5 w-3.5 mr-1.5" />
                  Data Intelligence
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-zinc-900 tracking-tight mb-6"
              >
                DataIntel
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg md:text-xl text-zinc-600 leading-relaxed mb-8"
              >
                The most comprehensive, accurate, and ML-ready database of Canadian nonprofit 
                organizations and grant programs. The data foundation powering all Impact Plexus products.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/dashboard">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8"
                    data-testid="dataintel-cta"
                  >
                    Explore Data
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-zinc-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold text-zinc-900">Data Coverage</h3>
                  <Badge className="bg-blue-100 text-blue-700">Live</Badge>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {dataStats.map((stat, i) => (
                    <div key={i} className="p-4 bg-zinc-50 rounded-xl">
                      <p className="text-2xl font-heading font-bold text-zinc-900">{stat.value}</p>
                      <p className="text-sm font-medium text-zinc-700">{stat.label}</p>
                      <p className="text-xs text-zinc-500">{stat.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-600">Data Completeness</span>
                    <span className="text-sm font-bold text-zinc-900">87%</span>
                  </div>
                  <Progress value={87} className="h-2" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="dataintel-features">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100 border-0">
              Core Components
            </Badge>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              Data infrastructure for AI
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Four interconnected systems that power intelligent grant matching and nonprofit analytics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-zinc-50 rounded-2xl p-8 border border-zinc-100 hover:border-blue-200 transition-all"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-zinc-900 mb-2">{feature.title}</h3>
                    <p className="text-zinc-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7-Dimensional Framework */}
      <section className="py-20 md:py-32 bg-zinc-50">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100 border-0">
              ML Pipeline
            </Badge>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-zinc-900 tracking-tight mb-4">
              7-Dimensional Feature Framework
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 border border-zinc-100 shadow-lg"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {["Context", "Range", "Prioritization", "Normalization", "Time Horizon", "Bind", "Bind Strength"].map((dim, i) => (
                <div key={dim} className="p-3 bg-blue-50 rounded-lg text-center">
                  <span className="text-xs font-medium text-blue-700">{i + 1}. {dim}</span>
                </div>
              ))}
            </div>

            <div className="p-6 bg-zinc-900 rounded-xl">
              <p className="text-xs text-zinc-400 mb-2">Effective Weight Calculation</p>
              <p className="text-lg font-mono text-white">
                Priority × Normalization × (Bind_Strength / 100)
              </p>
            </div>

            <div className="mt-6 grid md:grid-cols-5 gap-4 text-sm">
              {["Entity Identity", "Financial Health", "Grant Behavior", "Track Record", "Program Features"].map((group, i) => (
                <div key={group} className="text-center p-3 border border-zinc-200 rounded-lg">
                  <p className="font-medium text-zinc-900">Group {i + 1}</p>
                  <p className="text-xs text-zinc-500">{group}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-blue-600">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight mb-6">
            Data-driven decisions
          </h2>
          <p className="text-lg text-blue-100 mb-10 max-w-2xl mx-auto">
            Access insights from 83,000+ organizations and make informed strategic decisions.
          </p>
          <Link to="/dashboard">
            <Button
              size="lg"
              className="bg-white text-blue-700 hover:bg-zinc-100 font-medium px-8 py-6 text-base"
            >
              Explore DataIntel
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DataIntelPage;
